import { ClockCircleOutlined, DownloadOutlined, SyncOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { Button, Col, Row, Table, Tooltip } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { formatNumber, formatNumber2SigFig } from '../../../../../core/utils/campaigns';
import {
  getPacingTone,
  getAllTimeTone,
  getAllTimeOnPaceRatio,
  PacingDot,
  PACING_TONES,
} from '../../../Campaigns/shared/pacingVisuals';
import SelectCampaigns from './SelectCampaigns';
import { columnsObj } from './utils';
import ColumnsDropdown from './utils/ColumnsDropdown';
import prepareExport from './utils/prepareExport';
import { useTimezone } from '../../../../../core/hooks/useTimezone';

const MainView = ({
  tableData,
  isLoading,
  currentPagination,
  setPagination,
  total,
  onExport,
  campaignDataOptions,
  dspDataOptions,
  selectedCIDs,
  setSelectedCIDs,
  selectedDSP,
  setSelectedDSP,
  activeColumns,
  setActiveColumns,
  exportTableData,
  loadingExport,
  handleSorterChange,
  refetch,
}) => {
  const history = useHistory();
  const { timezone } = useTimezone();

  // Shared classic pacing preference (same key as the campaigns table,
  // cards, queue and modal — one toggle governs every surface).
  const [classicPacing, setClassicPacing] = useState(
    () => localStorage.getItem('pacingClassicView') === '1'
  );
  const toggleClassicPacing = () =>
    setClassicPacing((prev) => {
      localStorage.setItem('pacingClassicView', prev ? '0' : '1');
      return !prev;
    });

  // New-view column titles. This tab aggregates at the ORDER window
  // (PacingData rows per order), which is the right semantic for a
  // portfolio view — the labels just say so now.
  const PROJECTION_TOOLTIP =
    'Projected finish: (impressions × days remaining) + served impressions ÷ impression goal. Aggregated at the order window.';
  const projectionColumnKeys = [
    columnsObj.pace.key,
    columnsObj.paceYesterday.key,
    columnsObj.paceTwoDaysAgo.key,
    columnsObj.paceThreeDaysAgo.key,
    columnsObj.paceFourDaysAgo.key,
    columnsObj.paceFiveDaysAgo.key,
    columnsObj.paceSixDaysAgo.key,
    columnsObj.paceSevenDaysAgo.key,
  ];
  const newViewTitles = {
    [columnsObj.pace.key]: 'Projected finish (Today)',
    [columnsObj.paceYesterday.key]: 'Projected finish (Yesterday)',
    [columnsObj.paceTwoDaysAgo.key]: 'Projected finish (2 days ago)',
    [columnsObj.paceThreeDaysAgo.key]: 'Projected finish (3 days ago)',
    [columnsObj.paceFourDaysAgo.key]: 'Projected finish (4 days ago)',
    [columnsObj.paceFiveDaysAgo.key]: 'Projected finish (5 days ago)',
    [columnsObj.paceSixDaysAgo.key]: 'Projected finish (6 days ago)',
    [columnsObj.paceSevenDaysAgo.key]: 'Projected finish (7 days ago)',
  };

  const handleButtonClick = (record) => {
    history.push(`/campaigns/details/${record.id}`);
  };

  const columnRender = (key, value, record) => {
    if (key === columnsObj.Actions.key) {
      return (
        <Button type="primary" shape="round" onClick={() => handleButtonClick(record)}>
          VIEW
        </Button>
      );
    }
    if (key === columnsObj.daysIntoFlight.key || key === columnsObj.daysLeft.key) {
      if (value) {
        return <Text>{formatNumber(value)}</Text>;
      }
      return <Text>0</Text>;
    }

    if (
      [
        columnsObj.pace.key,
        columnsObj.paceAllTime.key,
        columnsObj.paceYesterday.key,
        columnsObj.paceTwoDaysAgo.key,
        columnsObj.paceThreeDaysAgo.key,
        columnsObj.paceFourDaysAgo.key,
        columnsObj.paceFiveDaysAgo.key,
        columnsObj.paceSixDaysAgo.key,
        columnsObj.paceSevenDaysAgo.key,
      ].includes(key)
    ) {
      if (!value) {
        return <Text type={classicPacing ? 'danger' : undefined}>0%</Text>;
      }

      // Classic: legacy thresholds and antd text types, verbatim
      if (classicPacing) {
        let type = '';
        switch (true) {
          case value >= 95:
            type = 'success';
            break;
          case value >= 91 && value <= 95:
            type = 'warning';
            break;
          case value >= 0 && value <= 90:
            type = 'danger';
            break;
          default:
            break;
        }
        return <Text type={type}>{formatNumber2SigFig(value)}%</Text>;
      }

      // New view: neutral ink + status dot in the calm palette. The
      // all-time column becomes the transparent on-pace ratio (delivered
      // ÷ expected-to-date) instead of raw % delivered.
      let display = value;
      let tone;
      if (key === columnsObj.paceAllTime.key) {
        const goal = record.impressionGoal > 0 ? record.impressionGoal : record.totalBudget || 0;
        const delivered =
          record.impressionGoal > 0
            ? record.totalImpressions || record.actualImpressions || 0
            : record.totalBudgetSpent || 0;
        const ratio = getAllTimeOnPaceRatio(
          goal,
          delivered,
          record.daysIntoFlight,
          record.daysLeft
        );
        if (ratio !== null) display = ratio;
        tone = getAllTimeTone(ratio);
      } else {
        const gracePeriod =
          value < 91 && (record.daysIntoFlight || 0) > 0 && record.daysIntoFlight < 10;
        tone = getPacingTone(value, { gracePeriod });
      }
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <PacingDot tone={tone} />
          <span
            style={{
              color: PACING_TONES.ink,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatNumber2SigFig(display)}%
          </span>
        </span>
      );
    }

    if (key === columnsObj.roas.key || key === columnsObj.ctr.key) {
      if (value) {
        return <Text>{formatNumber2SigFig(value)}%</Text>;
      }
      return <Text>0%</Text>;
    }

    if ([
      columnsObj.startDate.key,
      columnsObj.endDate.key,
      columnsObj.pacingStartDate.key,
      columnsObj.pacingEndDate.key,
      columnsObj.lastDataDate.key,
    ].includes(key)) {
      return <Text>{value ? moment(value).tz(timezone).format('MM/DD/YYYY') : '-'}</Text>;
    }

    // Tooltip for longer text characters
    return (
      <Tooltip title={value}>
        <Text>{value}</Text>
      </Tooltip>
    );
  };

  const columns = Object.entries(columnsObj)
    .map((c) => c[1])
    .map((c) => ({
      ...c,
      // New view: honest labels — these columns are projections and their
      // tooltips have always said so. Classic keeps the legacy titles.
      title: (() => {
        if (classicPacing) return c.title;
        if (c.key === columnsObj.paceAllTime.key) {
          return (
            <Tooltip title="Delivered ÷ expected to date (goal × elapsed days ÷ total days) — ~100% = on pace. Aggregated at the order window.">
              All-time · on pace to date
            </Tooltip>
          );
        }
        if (newViewTitles[c.key]) {
          return (
            <Tooltip title={PROJECTION_TOOLTIP}>{newViewTitles[c.key]}</Tooltip>
          );
        }
        return c.title;
      })(),
      render: (value, record) => columnRender(c.key, value, record),
    }))
    .filter((c) => {
      return activeColumns.indexOf(c.key) >= 0 || c.key === columnsObj.Actions.key;
    });

  columns.push({
    key: 'additional',
    width: '5%',
    title: () => {
      return <ColumnsDropdown selected={activeColumns} setSelected={setActiveColumns} />;
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    if (currentPagination.pageSize !== pagination.pageSize) {
      setPagination({ ...pagination, current: 1 });
    } else {
      setPagination(pagination);
    }
    handleSorterChange(sorter.columnKey, sorter.order);
  };

  const onRefresh = () => {
    refetch();
  };

  useEffect(() => {
    if (exportTableData) {
      prepareExport(exportTableData);
    }
  }, [exportTableData]);

  return (
    <React.Fragment>
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24} md={10} xl={7}>
          <SelectCampaigns
            campaignDataOptions={campaignDataOptions}
            selectedCIDs={selectedCIDs}
            setSelectedCIDs={setSelectedCIDs}
          />
        </Col>
        <Col xs={24} md={{ span: 5, offset: 2 }} xl={{ span: 3, offset: 9 }}>
          <Button
            style={{ width: '100%', display: 'block' }}
            type="primary"
            shape="round"
            onClick={onRefresh}
            icon={<SyncOutlined width="100" />}
            loading={isLoading}
          >
            Refresh
          </Button>
        </Col>
        <Col xs={24} md={{ span: 5 }} xl={{ span: 3 }}>
          <Button
            style={{ width: '100%', display: 'block' }}
            type="primary"
            shape="round"
            onClick={onExport}
            icon={<DownloadOutlined width="100" />}
            loading={loadingExport}
          >
            Export
          </Button>
        </Col>
        {/* Classic escape hatch — same clock-icon pattern and shared
            preference as the campaigns table, cards, queue and modal */}
        <Col xs={24} md={{ span: 2 }} xl={{ span: 1 }} style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Classic pacing view — legacy 'Pace' display" mouseEnterDelay={0.3}>
            <Button
              type="text"
              icon={<ClockCircleOutlined />}
              onClick={toggleClassicPacing}
              style={{ color: classicPacing ? '#1890ff' : '#8c8c8c' }}
            />
          </Tooltip>
        </Col>
      </Row>
      <Row>
        <Table
          style={{ width: '100%' }}
          scroll={{ x: 2000 }}
          onChange={handleTableChange}
          loading={isLoading}
          pagination={{
            ...currentPagination,
            showSizeChanger: total > 10,
            total,
            pageSizeOptions: [10, 20, 50, 100],
          }}
          dataSource={tableData}
          columns={columns}
          showSorterTooltip
        />
      </Row>
    </React.Fragment>
  );
};

export default MainView;
